import React, { useState, useEffect } from 'react';
import { func } from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import { I18n } from 'react-redux-i18n';

import { ReactComponent as UploadCloudIcon } from '../../assets/icons/upload-cloud.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';

import FormikInput from '../reusable/FormComponents/FormikInput';
import TextArea from '../reusable/FormComponents/TextArea';
import CustomButton from '../reusable/Buttons/Button';
import { errorMessages } from '../../constants/errorMessages';
import { submitSupportTicket, uploadSupportTicketAttachments } from '../../services/supportTicketService';

const SUPPORT_EMAIL = 'info@psytech.com';

const MAX_FILES = 5;

const formatFileSize = (bytes) => {
  if (!bytes) return '0 kB';
  const units = ['B', 'kB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** exponent;
  return `${exponent === 0 ? size : size.toFixed(1)} ${units[exponent]}`;
};

const SupportTicketForm = ({ onSubmitted }) => {
  const user = useSelector((state) => state.user.user);
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);

  const { values, handleChange, handleBlur, errors, touched, handleSubmit } = useFormik({
    initialValues: {
      email: user?.userName || '',
      subject: '',
      description: '',
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(errorMessages.emailField)
        .required(errorMessages.inputField),
      subject: Yup.string().required(errorMessages.inputField),
      description: Yup.string().required(errorMessages.inputField),
    }),
    onSubmit: async ({ email, subject, description }) => {
      setIsSubmitting(true);
      setHasSubmitError(false);
      try {
        const { data } = await submitSupportTicket({
          subject,
          body: description,
          channel: 'web',
          priority: 'normal',
          custom_fields: {
            cf_client: user?.distributorUserName || '',
            cf_distributorid: user?.distributorOwnerID || 0,
            cf_account: user?.accountName || '',
            cf_accountid: user?.accountID || 0,
            cf_userid: user?.userID || 0,
            cf_clientuser: email,
          },
        });
        onSubmitted();

        // The ticket is already created at this point, so an attachment upload failure
        // shouldn't surface as a submit error - the agent just won't see the files.
        if (files.length && data?.message_id) {
          try {
            await uploadSupportTicketAttachments(data.message_id, files);
          } catch (attachmentError) {
            // eslint-disable-next-line no-console
            console.error('Failed to upload support ticket attachments', attachmentError);
          }
        }
      } catch (e) {
        setHasSubmitError(true);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const [isDragActive, setIsDragActive] = useState(false);

  const { getRootProps, getInputProps } = useDropzone({
    noKeyboard: true,
    maxFiles: MAX_FILES,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDrop: () => setIsDragActive(false),
    onDropAccepted: (acceptedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles].slice(0, MAX_FILES));
    },
  });

  useEffect(() => {
    // The browser's native file picker opened via `open()` can leave react-dropzone's
    // internal drag state stuck "active" (a stray dragenter with no matching dragleave
    // once the dialog closes). Force it off whenever the window regains focus.
    const resetDragState = () => setIsDragActive(false);
    window.addEventListener('focus', resetDragState);
    return () => window.removeEventListener('focus', resetDragState);
  }, []);

  const removeFile = (fileToRemove) => {
    setFiles((prev) => prev.filter((file) => file !== fileToRemove));
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Title>{I18n.t('Contact us')}</Title>
      <FieldWrapper>
        <FormikInput
          inputName={I18n.t('Email')}
          errors={errors}
          touched={touched}
          type="text"
          name="email"
          id="email"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FormikInput
          inputName={I18n.t('Subject')}
          errors={errors}
          touched={touched}
          type="text"
          name="subject"
          id="subject"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.subject}
        />
      </FieldWrapper>
      <TextAreaWrapper>
        <TextArea
          inputName={I18n.t('Description')}
          errors={errors}
          touched={touched}
          name="description"
          id="description"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
        />
      </TextAreaWrapper>
      <DropZone {...getRootProps()} isActive={isDragActive}>
        <input {...getInputProps()} />
        <UploadCloudIcon />
        <DropZoneTitle>{I18n.t('Upload files (max %{max})', { max: MAX_FILES })}</DropZoneTitle>
        <DropZoneLabel>{I18n.t('Click to add or drag & drop files_')}</DropZoneLabel>
      </DropZone>
      {!!files.length && (
        <>
          <AttachmentsLabel>
            {files.length} {I18n.t(files.length === 1 ? 'ATTACHMENT' : 'ATTACHMENTS')}
          </AttachmentsLabel>
          <FilesList>
            {files.map((file) => (
              <FileItem key={`${file.name}-${file.size}`}>
                <FileInfo>
                  <FileName>{file.name}</FileName>
                  <FileSize>{formatFileSize(file.size)}</FileSize>
                </FileInfo>
                <RemoveFileButton type="button" onClick={() => removeFile(file)}>
                  <TrashIcon />
                </RemoveFileButton>
              </FileItem>
            ))}
          </FilesList>
        </>
      )}
      {hasSubmitError && (
        <ErrorBanner>
          {I18n.t('Submit failed_ Please try again or contact us at')}{' '}
          <ErrorBannerLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</ErrorBannerLink>
        </ErrorBanner>
      )}
      <SendButton type="submit" loading={isSubmitting} disabled={isSubmitting}>
        {I18n.t('Send')}
      </SendButton>
    </Form>
  );
};

SupportTicketForm.propTypes = {
  onSubmitted: func.isRequired,
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: bold;
  color: ${(props) => props.theme.colors.darkBlue2};
  margin-bottom: 2rem;
`;

const FieldWrapper = styled.div`
  margin-bottom: 1.6rem;
`;

const TextAreaWrapper = styled.div`
  margin-bottom: 1.6rem;

  > div {
    height: 12rem;
  }
`;

const DropZone = styled.div`
  border: 2px dashed ${(props) => (props.isActive ? props.theme.colors.lightBlue : props.theme.colors.grey2)};
  background-color: ${(props) => (props.isActive ? props.theme.colors.lightBlue1 : props.theme.colors.grey1)};
  border-radius: 0.8rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: 0.2s all;
  margin-bottom: 1.6rem;

  :hover {
    cursor: pointer;
  }

  svg {
    margin-bottom: 0.8rem;
  }
`;

const DropZoneTitle = styled.span`
  font-size: ${(props) => props.theme.fontSizes.default};
  font-weight: bold;
  color: ${(props) => props.theme.colors.darkBlue2};
  margin-bottom: 0.4rem;
`;

const DropZoneLabel = styled.span`
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.grey4};

  ${DropZone}:hover & {
    text-decoration: underline;
  }
`;

const AttachmentsLabel = styled.span`
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: bold;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.grey4};
  margin-bottom: 0.8rem;
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.colors.grey2};
  border-radius: 0.8rem;
  margin-bottom: 1.6rem;
  overflow: hidden;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 1.6rem;

  :not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.colors.grey2};
  }
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FileName = styled.span`
  font-size: ${(props) => props.theme.fontSizes.normal};
  font-weight: bold;
  color: ${(props) => props.theme.colors.darkBlue2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.grey4};
  margin-top: 0.4rem;
`;

const RemoveFileButton = styled.button`
  border: none;
  outline: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 1.6rem;
  flex-shrink: 0;

  svg {
    width: 1.8rem;
    height: 1.8rem;

    path {
      transition: stroke 0.2s;
    }
  }

  :hover {
    cursor: pointer;

    svg path {
      stroke: ${(props) => props.theme.colors.red};
    }
  }
`;

const SendButton = styled(CustomButton)`
  width: 100%;
`;

const ErrorBanner = styled.div`
  background-color: #fdeceb;
  border: 1px solid ${(props) => props.theme.colors.red};
  border-radius: 0.4rem;
  padding: 1.2rem 1.6rem;
  margin-bottom: 1.6rem;
  color: ${(props) => props.theme.colors.red};
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: 500;
`;

const ErrorBannerLink = styled.a`
  color: ${(props) => props.theme.colors.red};
  font-weight: bold;
  text-decoration: underline;
`;

export default SupportTicketForm;
